import RestControl from "./RestControl";
import SetRow from "./SetRow";
import TempoControl from "./TempoControl";
import BlockRevealRegion from "./BlockRevealRegion";
import BlockDragHandle, {
  BlockBodyAction,
  BlockBodyActions,
  BlockIconButton,
  BlockThumbnail,
  ChevronPath,
  DuplicatePath,
  LANE_THUMBNAIL_SIZE,
  TrashPath,
} from "./blockChrome";
import {
  isPlannedExerciseNoteTooLong,
  PLANNED_EXERCISE_NOTE_MAX_LENGTH,
  PLANNED_EXERCISE_NOTE_TOO_LONG_COPY,
} from "./plannedExerciseNote";

// Tier-3 builder presentation seam (Phase 7A) -- Design V2 superset block.
//
// Presentation only, and the superset data model is preserved exactly: two
// identified lanes (A1 / A2), lane-specific exercise assignment, lane-specific
// tempo, lane-specific set rows and rep prescriptions, a shared parent round
// count that the container normalizes across lanes, parent rest, and the
// incomplete-superset state. An incomplete superset stays incomplete -- this
// component never fabricates a second exercise.
//
// `data-superset-block` is load-bearing: the container's outside-click handler
// targets the lane being assigned through it.
export default function BlockEditorSuperset({
  block,
  blockUiKey,
  isDragging,
  disabled = false,
  isIncompleteSuperset,
  maxSetCount,
  activeSearchTarget,
  openLaneIndex,
  tempoValueForLane,
  repDraftsForLaneSet,
  prescriptionForSet,
  sortable,
  onToggleLane,
  onDelete,
  onDuplicate,
  onRemoveLaneExercise,
  onSelectLaneExercise,
  onLaneTempoFocus,
  onLaneTempoChange,
  onLaneTempoBlur,
  onLaneNotesChange,
  onCycleRir,
  onRepFieldFocus,
  onRepFieldChange,
  onRepFieldBlur,
  onRoundsChange,
  onRestStep,
}) {
  const rounds = block.sets || 1;
  // "Editing" means a lane is open. It reads the container's existing lane
  // state; no new expansion state is introduced.
  const isAnyLaneOpen = block.exercises.some(
    (_, exerciseIndex) => openLaneIndex === exerciseIndex
  );

  return (
    <section
      ref={sortable.setNodeRef}
      style={sortable.style}
      data-superset-block={block.id}
      data-block-id={blockUiKey}
      data-dragging={isDragging ? "true" : undefined}
      data-incomplete={isIncompleteSuperset ? "true" : undefined}
      className="lz-builder-block lz-builder-block--superset"
    >
      {/* There is no separate parent header to open first: the compact card IS
          the two lanes, sharing ONE surface behind ONE cyan rail so they read
          as a single linked pair. The rail is decorative; the A1/A2 data
          semantics are untouched. The block is named ONCE at the top, and ONE
          block-level drag activator sits top-right above both lane chevrons --
          the lanes carry none, because inner reordering is not implemented. */}
      <div className="lz-builder-superset__header">
        <p className="lz-builder-superset__label">Superset</p>

        <BlockDragHandle
          disabled={disabled}
          setHandleRef={sortable.setHandleRef}
          handleAttributes={sortable.handleAttributes}
          handleListeners={sortable.handleListeners}
        />
      </div>

      <div className="lz-builder-superset-group">
        <span className="lz-builder-superset-group__rail" aria-hidden="true" />

        <div className="lz-builder-superset-group__lanes">
          {isIncompleteSuperset ? (
            <p className="lz-builder-block__meta lz-builder-block__meta--warning lz-builder-superset-group__status">
              Incomplete superset
            </p>
          ) : null}

          {block.exercises.map((exercise, exerciseIndex) => {
            const laneKey = `${block.id}-${exerciseIndex}`;
            // An unassigned lane has no exercise editor to open, so it offers no
            // toggle at all -- and it can never render as open, even if a lane
            // that WAS open has just been cleared.
            const isLaneEmpty = !String(exercise.exerciseId || "").trim();
            const laneCollapsed = isLaneEmpty || openLaneIndex !== exerciseIndex;
            const laneTempoId = `lz-tempo-${blockUiKey}-${exerciseIndex}`;
            const isAssigning =
              activeSearchTarget?.blockId === block.id &&
              activeSearchTarget?.exerciseIndex === exerciseIndex;

            const laneSetCount = exercise.sets?.length || 0;
            const measurementType = exercise.executionProfile?.measurementType || "REPS";
            const isTimed = measurementType === "TIMED_HOLD";

            return (
              <div
                className="lz-builder-lane"
                key={laneKey}
                data-lane-label={exercise.label}
              >
                {/* Column order leaves room for a future inner drag handle
                    ahead of the thumbnail; inner reorder is deliberately NOT
                    implemented here. */}
                <div className="lz-builder-lane__header">
                  <BlockThumbnail
                    exerciseId={exercise.exerciseId}
                    size={LANE_THUMBNAIL_SIZE}
                  />

                  <div className="lz-builder-lane__identity">
                    {isLaneEmpty ? (
                      // EITHER lane can be empty now that a populated one can be
                      // cleared, so the select affordance follows the empty lane
                      // rather than assuming it is always A2. Same callback, same
                      // `activeSearchTarget`, same search.
                      <button
                        type="button"
                        data-lane-target={laneKey}
                        disabled={disabled}
                        onClick={() => onSelectLaneExercise(block.id, exerciseIndex)}
                        aria-pressed={isAssigning}
                        className="lz-builder-lane__select"
                      >
                        {exerciseIndex === 0
                          ? "Select first exercise"
                          : "Select second exercise"}
                      </button>
                    ) : (
                      <>
                        <p className="lz-builder-lane__name">{exercise.name}</p>
                        <p className="lz-builder-lane__meta">
                          {laneSetCount} {laneSetCount === 1 ? "set" : "sets"}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="lz-builder-lane__controls">
                  {/* Removing ONE exercise is a lane-level action, so it is
                      offered only while that lane is open for editing and sits
                      immediately before its own toggle. It clears the lane; it
                      never deletes the superset or converts it to a single. */}
                  {laneCollapsed ? null : (
                    <BlockIconButton
                      label={`Remove ${exercise.label} exercise`}
                      tone="danger"
                      disabled={disabled}
                      onClick={() => onRemoveLaneExercise(exerciseIndex)}
                    >
                      <TrashPath />
                    </BlockIconButton>
                  )}

                  {isLaneEmpty ? null : (
                    <BlockIconButton
                      label={
                        laneCollapsed
                          ? `Expand ${exercise.label}`
                          : `Collapse ${exercise.label}`
                      }
                      expanded={!laneCollapsed}
                      onClick={() => onToggleLane(exerciseIndex)}
                    >
                      <ChevronPath collapsed={laneCollapsed} />
                    </BlockIconButton>
                  )}
                  </div>
                </div>

                <BlockRevealRegion open={!laneCollapsed}>
                  {() => (
                  <div className="lz-builder-lane__body">
                    {isTimed ? null : <div className="lz-builder-field-group">
                      <label className="lz-builder-field-label" htmlFor={laneTempoId}>
                        Tempo
                      </label>
                      <TempoControl
                        id={laneTempoId}
                        value={tempoValueForLane(exerciseIndex, exercise)}
                        disabled={disabled}
                        onFocus={() => onLaneTempoFocus(exerciseIndex, exercise)}
                        onChange={(value) => onLaneTempoChange(exerciseIndex, value)}
                        onBlur={() => onLaneTempoBlur(exerciseIndex, exercise)}
                      />
                    </div>}

                    <div className="lz-builder-set-table">
                      <div className="lz-builder-set-table__head" aria-hidden="true">
                        <span>Set</span>
                        <span>{isTimed ? "Time" : "Reps"}</span>
                        <span>{isTimed ? "" : "RIR"}</span>
                        <span />
                      </div>

                      <div className="lz-builder-set-table__rows">
                        {exercise.sets.map((set, setIndex) => (
                          <SetRow
                            key={`${laneKey}-set-${setIndex}`}
                            setNumber={setIndex + 1}
                            label={`${exercise.label} set ${setIndex + 1}`}
                            prescription={prescriptionForSet(set, measurementType)}
                            repDrafts={repDraftsForLaneSet(exerciseIndex, setIndex)}
                            rirValue={set.rpe}
                            measurementType={measurementType}
                            disabled={disabled}
                            onCycleRir={() => onCycleRir(exerciseIndex, setIndex, set.rpe)}
                            onRepFieldFocus={(field) =>
                              onRepFieldFocus(exerciseIndex, setIndex, set, field)
                            }
                            onRepFieldChange={(field, value) =>
                              onRepFieldChange(exerciseIndex, setIndex, field, value)
                            }
                            onRepFieldBlur={(field) =>
                              onRepFieldBlur(exerciseIndex, setIndex, set, field)
                            }
                          />
                        ))}
                      </div>
                    </div>

                    <textarea
                      disabled={disabled}
                      value={exercise.notes || ""}
                      maxLength={PLANNED_EXERCISE_NOTE_MAX_LENGTH}
                      onChange={(event) =>
                        onLaneNotesChange(exerciseIndex, event.target.value)
                      }
                      placeholder="Execution focus (e.g. 2s pause at peak contraction)..."
                      aria-label={`${exercise.label} notes`}
                      className="lz-builder-notes"
                    />
                    {isPlannedExerciseNoteTooLong(exercise.notes) ? (
                      <p className="lz-builder-block__hint" role="status">{PLANNED_EXERCISE_NOTE_TOO_LONG_COPY}</p>
                    ) : null}
                  </div>
                  )}
                </BlockRevealRegion>
              </div>
            );
          })}

          {/* Shared superset controls stay INSIDE the railed group, so the
              cyan relationship runs through the whole editor rather than
              leaving them looking like an unrelated footer. They appear only
              while a lane is open: the compact card stays just the two
              exercises. Same data, same callbacks, same
              `openSupersetExerciseByBlock` state. */}
          <BlockRevealRegion open={isAnyLaneOpen}>
            {() => (
            <div className="lz-builder-superset-group__shared">
              <div className="lz-builder-block__controls">
            <div className="lz-builder-field-group">
              <span className="lz-builder-field-label">Sets</span>
              <div className="lz-builder-stepper">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRoundsChange(Math.max(1, rounds - 1))}
                  className="lz-builder-stepper__button"
                  aria-label="Decrease superset rounds"
                >
                  <span aria-hidden="true">&minus;</span>
                </button>
                <span className="lz-builder-stepper__value">{block.sets}</span>
                <button
                  type="button"
                  disabled={disabled || (block.sets || 0) >= maxSetCount}
                  onClick={() => onRoundsChange((block.sets || 0) + 1)}
                  className="lz-builder-stepper__button"
                  aria-label="Increase superset rounds"
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
            </div>

            <div className="lz-builder-field-group">
              <span className="lz-builder-field-label">Rest Interval</span>
              <RestControl
                value={block.rest}
                disabled={disabled}
                onStep={onRestStep}
              />
            </div>
          </div>

              {/* Same delete callback as before, inside the group. Delete here
                  is the WHOLE superset -- distinct from a lane's own
                  remove-exercise control, which sits up on the open lane's
                  header and only clears that one lane. */}
              <BlockBodyActions
                trailing={
                  <BlockBodyAction
                    label="Delete Superset"
                    tone="danger"
                    iconOnly
                    disabled={disabled}
                    onClick={onDelete}
                  >
                    <TrashPath />
                  </BlockBodyAction>
                }
              >
                <BlockBodyAction
                  label="Duplicate Block"
                  disabled={disabled}
                  onClick={onDuplicate}
                >
                  <DuplicatePath />
                </BlockBodyAction>
              </BlockBodyActions>
            </div>
            )}
          </BlockRevealRegion>
        </div>
      </div>
    </section>
  );
}
